import React, { useEffect, useMemo, useState } from "react";
import { ReactFlow, Background, Controls, Handle, Position } from "@xyflow/react";
import "@xyflow/react/dist/style.css";
import { LineChart, Line, XAxis, ResponsiveContainer, Tooltip } from "recharts";
import { api } from "@/lib/api";
import { CP, DOMAIN_COLORS, CapCard, Pill } from "./ui";
import PersonNetwork from "./PersonNetwork";

function SkillNode({ data }) {
  return (
    <div className="flex flex-col items-center" style={{ opacity: data.dim ? 0.25 : 1 }}>
      <Handle type="target" position={Position.Top} style={{ opacity: 0 }} />
      <div className="rounded-full flex items-center justify-center text-white font-bold shadow-md"
        style={{
          width: data.size, height: data.size, background: data.color, fontSize: 11,
          border: data.selected ? "3px solid #12275A" : "2px solid #fff",
        }}>
        {data.label.slice(0, 2).toUpperCase()}
      </div>
      <div className="text-[10px] font-semibold mt-1 capitalize text-center max-w-[90px]" style={{ color: CP.text }}>{data.label}</div>
      <Handle type="source" position={Position.Bottom} style={{ opacity: 0 }} />
    </div>
  );
}
const NODE_TYPES = { skillNode: SkillNode };

export default function GraphExplorer() {
  const [mode, setMode] = useState("capabilities");
  const [catalog, setCatalog] = useState({ skills: [], edges: [] });
  const [selected, setSelected] = useState(null);
  const [detail, setDetail] = useState(null);
  const [filter, setFilter] = useState("");

  useEffect(() => {
    api.get("/capability/skills/catalog").then(({ data }) => {
      setCatalog(data);
      if (data.skills?.length) loadDetail(data.skills[0].skill);
    }).catch(() => {});
  }, []);

  const loadDetail = (skill) => {
    setSelected(skill);
    api.get(`/capability/skill/${encodeURIComponent(skill)}`).then(({ data }) => setDetail(data)).catch(() => {});
  };

  const { nodes, edges } = useMemo(() => {
    const top = catalog.skills.slice(0, 18);
    const maxSup = Math.max(1, ...top.map((s) => s.supply));
    const cx = 380, cy = 280;
    const nodes = top.map((s, i) => {
      const angle = (2 * Math.PI * i) / top.length - Math.PI / 2;
      const radius = i === 0 ? 0 : 160 + (i % 3) * 70;
      return {
        id: s.skill, type: "skillNode",
        position: { x: cx + Math.cos(angle) * radius, y: cy + Math.sin(angle) * radius },
        data: {
          label: s.skill, color: DOMAIN_COLORS[s.domain] || CP.sky,
          size: 34 + Math.round((s.supply / maxSup) * 26),
          selected: s.skill === selected,
          dim: filter && !s.skill.includes(filter.toLowerCase()),
        },
      };
    });
    const ids = new Set(top.map((s) => s.skill));
    const maxW = Math.max(1, ...catalog.edges.map((e) => e.w));
    const edges = catalog.edges.filter((e) => ids.has(e.a) && ids.has(e.b)).map((e, i) => ({
      id: `e${i}`, source: e.a, target: e.b,
      style: { stroke: "#C7D2E8", strokeWidth: 1 + (e.w / maxW) * 3 },
    }));
    return { nodes, edges };
  }, [catalog, selected, filter]);

  return (
    <div className="space-y-4" data-testid="graph-explorer">
      <div className="flex flex-wrap items-center gap-3">
        <input value={filter} onChange={(e) => setFilter(e.target.value)} placeholder="Search capabilities…"
          data-testid="explorer-search" className="h-10 px-3 rounded-lg text-sm bg-white outline-none w-64"
          style={{ border: `1px solid ${CP.border}`, color: CP.text }} />
        <div className="ml-auto flex rounded-lg overflow-hidden" style={{ border: `1px solid ${CP.border}` }}>
          {["capabilities", "people"].map((m) => (
            <button key={m} onClick={() => setMode(m)} data-testid={`explorer-mode-${m}`}
              className="px-4 py-2 text-xs font-semibold capitalize transition-colors"
              style={{ background: mode === m ? CP.navy : "#fff", color: mode === m ? "#fff" : CP.sub }}>
              {m === "capabilities" ? "Capability Graph" : "Person Network"}
            </button>
          ))}
        </div>
      </div>

      {mode === "people" ? <PersonNetwork /> : (
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
          <div className="lg:col-span-2 rounded-xl bg-white shadow-sm overflow-hidden" style={{ border: `1px solid ${CP.border}`, height: 560 }} data-testid="explorer-canvas">
            <ReactFlow nodes={nodes} edges={edges} nodeTypes={NODE_TYPES} fitView
              fitViewOptions={{ padding: 0.15 }} proOptions={{ hideAttribution: true }}
              onNodeClick={(_, n) => loadDetail(n.id)} nodesDraggable={false}>
              <Background gap={18} color="#E8EDF7" />
              <Controls showInteractive={false} />
            </ReactFlow>
          </div>

          <CapCard title="Details" testid="explorer-details">
            {detail ? (
              <div className="space-y-4">
                <div>
                  <div className="flex items-center gap-2">
                    <span className="text-base font-bold capitalize" style={{ color: CP.text }}>{detail.skill}</span>
                    {detail.core && <Pill tone="purple">Core Capability</Pill>}
                  </div>
                  <p className="text-xs mt-1.5 leading-relaxed" style={{ color: CP.sub }}>{detail.description}</p>
                </div>
                <div className="space-y-2.5 text-sm">
                  <DetailRow label="Related Capabilities" value={detail.related_count} />
                  <DetailRow label="People with this Skill" value={detail.people_count.toLocaleString()} />
                  <DetailRow label="Open Demand" value={detail.open_demand} />
                  <div className="flex items-center justify-between">
                    <span className="text-xs" style={{ color: CP.sub }}>Avg Proficiency</span>
                    <span className="flex items-center gap-2">
                      <span className="w-20 h-1.5 rounded-full" style={{ background: "#EDF1F9" }}>
                        <span className="block h-full rounded-full" style={{ width: `${(detail.avg_prof / 5) * 100}%`, background: CP.purple }} />
                      </span>
                      <span className="text-sm font-semibold" style={{ color: CP.text }}>{detail.avg_prof}/5</span>
                    </span>
                  </div>
                </div>
                <div>
                  <div className="text-xs font-semibold mb-1" style={{ color: CP.sub }}>Demand Trend (Last 6 Months)</div>
                  <div style={{ width: "100%", height: 90, minHeight: 90 }}>
                    <ResponsiveContainer minHeight={90}>
                      <LineChart data={detail.demand_trend}>
                        <XAxis dataKey="month" tick={{ fontSize: 9, fill: CP.sub }} tickFormatter={(m) => m.slice(5)} />
                        <Tooltip contentStyle={{ fontSize: 11, borderRadius: 8 }} />
                        <Line type="monotone" dataKey="demand" stroke={CP.purple} strokeWidth={2} dot={{ r: 2 }} />
                      </LineChart>
                    </ResponsiveContainer>
                  </div>
                </div>
                <div className="flex flex-wrap gap-1.5">
                  {detail.adjacent.slice(0, 5).map((a) => (
                    <button key={a.skill} onClick={() => loadDetail(a.skill)}
                      className="text-[11px] px-2 py-1 rounded-full capitalize hover:opacity-80"
                      style={{ background: "#EFF4FF", color: CP.blue, border: `1px solid #BFD3FE` }}
                      data-testid={`explorer-adjacent-${a.skill}`}>{a.skill}</button>
                  ))}
                </div>
              </div>
            ) : <div className="text-xs py-8 text-center" style={{ color: CP.sub }}>Click a node to inspect.</div>}
          </CapCard>
        </div>
      )}

      {mode === "capabilities" && (
        <div className="flex flex-wrap gap-4 text-[11px]" style={{ color: CP.sub }}>
          {Object.entries(DOMAIN_COLORS).map(([d, c]) => (
            <span key={d} className="flex items-center gap-1.5">
              <span className="w-2.5 h-2.5 rounded-full inline-block" style={{ background: c }} />{d}
            </span>
          ))}
          <span className="ml-auto">Node size = number of people · Edge weight = co-occurrence in work</span>
        </div>
      )}
    </div>
  );
}

function DetailRow({ label, value }) {
  return (
    <div className="flex items-center justify-between">
      <span className="text-xs" style={{ color: CP.sub }}>{label}</span>
      <span className="text-sm font-semibold" style={{ color: CP.text }}>{value}</span>
    </div>
  );
}
